import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { REPORTING_LE_LENSES_PATH } from '~/consts/reportingLeLensApi';

import { request } from '~/services/common';

import type ApiError from '~/types/ApiError';
import type { Lens, LensResponse, LensView } from '~/types/leLens';

import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { encodedJsonBody } from './encodedJsonBody';
import { reportingLeLensQueryKeys, useGetReportingLeLens } from './useGetReportingLeLens';
import { refreshCertifiedShelves } from './useLensCertification';

export type LensOperation = { type: string; id?: string; changes?: Record<string, unknown>; unset?: Array<string> };

export type LensChangedCard = {
  id: string;
  title: string;
  change: 'added' | 'removed' | 'updated';
  fields: Array<string>;
};

export type LensAppliedChange = {
  kind: 'lens_applied';
  lensId: string;
  applied: boolean;
  fromVersion: number;
  toVersion: number;
  changed: { cards: Array<LensChangedCard>; lens: Array<string>; datasets: Array<string> };
  problems: Array<{ path: string; kind: string; message: string }>;
  undoes?: Array<number>;
  view?: { change: number };
};

type LensChangeRequest =
  | { baseVersion: number; operations: Array<LensOperation> }
  | { action: 'undo' | 'inspect'; versions: Array<number> };

export type LensChangeResponse = LensAppliedChange & { lens?: Lens };

const changesUrl = (lensId: string) =>
  resolveReportingLeLensUrl(`${REPORTING_LE_LENSES_PATH}/${encodeURIComponent(lensId)}/changes`);

const viewUrl = (lensId: string) =>
  resolveReportingLeLensUrl(`${REPORTING_LE_LENSES_PATH}/${encodeURIComponent(lensId)}/view`);

export function lensChangeFailure(error: unknown): string {
  const body = (error as ApiError | undefined)?.responseBody as { message?: string } | undefined;
  return body?.message ?? (error instanceof Error ? error.message : 'The change did not apply. Try again.');
}

export async function refreshLensAfterChange(client: QueryClient, lensId: string, lens?: Lens) {
  if (lens) {
    client.setQueryData<LensResponse>(reportingLeLensQueryKeys.detail(lensId), (current) =>
      current ? { ...current, lens } : current,
    );
  }
  await client.refetchQueries({ exact: true, queryKey: reportingLeLensQueryKeys.detail(lensId) });
  await client.refetchQueries({ queryKey: [...reportingLeLensQueryKeys.detail(lensId), 'data'] });
  await refreshCertifiedShelves(client);
}

export type LensUndoEntry = { lensId: string; next: Array<number>; undone: boolean };

const lensUndoKey = (lensId: string) => ['lens-undo', lensId] as const;

export const lensUndoEntryKey = (lensId: string, versions: ReadonlyArray<number>) =>
  [...lensUndoKey(lensId), [...versions].sort((a, b) => a - b).join(',')] as const;

async function postLensView(lensId: string, body: Record<string, unknown>) {
  return (await request(viewUrl(lensId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: encodedJsonBody(body),
  })) as { lens: Lens; view?: LensView } & Record<string, unknown>;
}

export async function postLensChange(lensId: string, body: LensChangeRequest) {
  return (await request(changesUrl(lensId), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: encodedJsonBody(body),
  })) as LensChangeResponse;
}

export function rememberOwnLensChange(client: QueryClient, receipt: LensAppliedChange) {
  if (!receipt.applied || receipt.undoes || receipt.view) return;
  client.setQueryDefaults(lensUndoKey(receipt.lensId), { gcTime: Infinity, staleTime: Infinity });
  client.setQueryData<LensUndoEntry>(lensUndoEntryKey(receipt.lensId, [receipt.toVersion]), {
    lensId: receipt.lensId,
    next: [receipt.toVersion],
    undone: false,
  });
}

/** Undo when the operation stands, Redo when it is undone; either way one request and one new state. */
export async function toggleLensUndo(client: QueryClient, key: ReadonlyArray<string>) {
  const entry = client.getQueryData<LensUndoEntry>(key);
  if (!entry) return undefined;
  const receipt = await postLensChange(entry.lensId, { action: 'undo', versions: entry.next });
  client.setQueryData<LensUndoEntry>(key, { ...entry, next: [receipt.toVersion], undone: !entry.undone });
  await refreshLensAfterChange(client, entry.lensId, receipt.lens);
  return client.getQueryData<LensUndoEntry>(key);
}

/**
 * The actor's operation on this Lens that Cmd/Ctrl+Z (`undone: false`) or Cmd/Ctrl+Shift+Z (`undone: true`)
 * acts on: the most recent standing one, or the most recently undone one since the actor's last new edit.
 */
export function latestLensUndo(client: QueryClient, lensId: string, undone: boolean) {
  const latest = (entry: LensUndoEntry) => Math.max(...entry.next);
  const entries = client
    .getQueriesData<LensUndoEntry>({ queryKey: lensUndoKey(lensId) })
    .filter((found): found is [ReadonlyArray<string>, LensUndoEntry] => Boolean(found[1]));
  if (!undone) {
    return entries
      .filter(([, entry]) => !entry.undone)
      .sort(([, left], [, right]) => latest(right) - latest(left))[0]?.[0];
  }
  const editedAt = (key: ReadonlyArray<string>) => Math.max(...String(key[key.length - 1]).split(',').map(Number));
  const lastEdit = Math.max(0, ...entries.map(([key]) => editedAt(key)));
  return entries
    .filter(([, entry]) => entry.undone && latest(entry) > lastEdit)
    .sort(([, left], [, right]) => latest(right) - latest(left))[0]?.[0];
}

export function useLensChange(lensId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { baseVersion: number; operations: Array<LensOperation> }) => postLensChange(lensId, body),
    onSuccess: async (receipt) => {
      rememberOwnLensChange(client, receipt);
      await refreshLensAfterChange(client, lensId, receipt.lens);
    },
    retry: false,
  });
}

export function useLensUndo(lensId: string, versions: ReadonlyArray<number>) {
  const client = useQueryClient();
  const key = lensUndoEntryKey(lensId, versions);
  const entry = useQuery({
    queryKey: key,
    enabled: versions.length > 0,
    gcTime: Infinity,
    staleTime: Infinity,
    retry: false,
    queryFn: async (): Promise<LensUndoEntry> => {
      const state = (await postLensChange(lensId, { action: 'inspect', versions: [...versions] })) as unknown as {
        next: Array<number>;
        undone: boolean;
      };
      return { lensId, next: state.next, undone: state.undone };
    },
  });
  const toggle = useMutation({ mutationFn: () => toggleLensUndo(client, key), retry: false });
  return { entry: entry.data, toggle };
}

/** A viewer's answer can be undone while its view changes stand; their view keeps no Redo. */
export function useLensViewUndo(lensId: string, changes: ReadonlyArray<number>) {
  const client = useQueryClient();
  const { data } = useGetReportingLeLens(lensId);
  const standing = changes.some((change) => data?.view?.changes.some((entry) => entry.id === change));
  const undo = useMutation({
    mutationFn: async () => {
      try {
        await postLensView(lensId, { action: 'undo', changes: [...changes] });
      } finally {
        await refreshLensAfterChange(client, lensId);
      }
    },
    retry: false,
  });
  return { standing, undo };
}

export function useLensView(lensId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { action: 'reset' }) => postLensView(lensId, body),
    onSuccess: () => refreshLensAfterChange(client, lensId),
    retry: false,
  });
}
